import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Card, CardBody, Row, Col, Input, Label, FormGroup, Button } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getVehicle, getVehicleOwners, createVehicle, updateVehicle, getFleetExpenseSettings } from "../../services/fleet"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import "../Tenancy/tenancy.css"

const EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"]
const VEHICLE_TYPES = ["BUS", "COASTER", "CAR", "PICK UP", "VAN"]

const blank = {
  owner_company_id: "", plate_emirate: "", plate_code: "", plate_no: "",
  vehicle_type: "", name_of_vehicle: "", model_year: "", vehicle_origin: "",
  tc_number: "", engine_no: "", chassis_no: "", seater: "",
  registration_date: "", mulkiya_expiry_date: "", insurance_policy_number: "", insurance_expiry_date: "",
  salik_tag_id: "", salik_account_no: "", vip_tag_no: "", gps_id: "",
  driver_name: "", category: "OWN", book_no: "", remarks: "", status: 1,
  opening_balance: "", depreciation_cost: "", expense_class: "worker", expense_group_id: "",
}

const d10 = v => (v ? String(v).slice(0, 10) : "")

/**
 * Operations → Fleet → New / Edit vehicle. Owner is a company flagged "Owns vehicles".
 * On save the backend auto-creates / keeps in sync a Fixed-Asset ledger for the vehicle.
 */
const FleetEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  document.title = `${isEdit ? "Edit" : "New"} Vehicle | Calyvern`
  const [form, setForm] = useState(blank)
  const [owners, setOwners] = useState([])
  const [expenseGroups, setExpenseGroups] = useState([]) // the groups ticked in Developer → Fleet Accounts
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await getVehicleOwners()
        setOwners(r?.data || [])
      } catch (e) { toastError(e?.message) }
      try {
        // Expense-account choices = the finance groups selected in Developer → Fleet Accounts
        const s = await getFleetExpenseSettings()
        const selected = (s?.data?.selected || []).map(Number)
        setExpenseGroups((s?.data?.groups || []).filter(g => selected.includes(Number(g.id))))
      } catch (e) { /* non-fatal */ }
    })()
  }, [])

  useEffect(() => {
    if (!isEdit) return
    ;(async () => {
      try {
        const r = await getVehicle(id)
        const v = r?.data
        if (v) setForm({
          ...blank, ...v,
          owner_company_id: v.owner_company_id || "",
          seater: v.seater ?? "",
          registration_date: d10(v.registration_date),
          mulkiya_expiry_date: d10(v.mulkiya_expiry_date),
          insurance_expiry_date: d10(v.insurance_expiry_date),
          opening_balance: v.opening_balance ?? "",
          depreciation_cost: v.depreciation_cost ?? "",
          expense_class: v.expense_class || "worker",
          expense_group_id: v.expense_group_id ? String(v.expense_group_id) : "",
          status: v.status ? 1 : 0,
        })
      } catch (e) { toastError(e?.message) }
    })()
  }, [id, isEdit])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.owner_company_id) return toastError("Owner company is required")
    if (!form.plate_no?.trim()) return toastError("Plate No. is required")
    try {
      setSaving(true)
      const res = isEdit ? await updateVehicle(id, form) : await createVehicle(form)
      toastSuccess(res?.message || "Vehicle saved")
      navigate("/fleet")
    } catch (e) { toastError(e?.message) } finally { setSaving(false) }
  }

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center me-2">
          <Breadcrumbs title="Operations" breadcrumbObject={[
            { title: "Operations", link: "#" },
            { title: "Fleet", link: "/fleet" },
            { title: isEdit ? "Edit vehicle" : "New vehicle", link: "#" },
          ]} />
          <div className="mb-3">
            <Button color="light" className="me-2" onClick={() => navigate("/fleet")}>Cancel</Button>
            <Button color="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : isEdit ? "Update" : "Save"}</Button>
          </div>
        </div>

        {/* Plate & Owner */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Plate &amp; Owner</h6>
          <Row>
            <Col md={4}><FormGroup><Label>Owner company<span className="mandatory">*</span></Label>
              <Input type="select" bsSize="sm" value={form.owner_company_id} onChange={e => set("owner_company_id", e.target.value)}>
                <option value="">Select owner</option>
                {owners.map(o => <option key={o.id} value={o.id}>{o.company_name}</option>)}
              </Input></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Emirate</Label>
              <Input type="select" bsSize="sm" value={form.plate_emirate} onChange={e => set("plate_emirate", e.target.value)}>
                <option value="">Select emirate</option>{EMIRATES.map(em => <option key={em} value={em}>{em}</option>)}
              </Input></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Plate code</Label><Input bsSize="sm" value={form.plate_code} onChange={e => set("plate_code", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Plate No.<span className="mandatory">*</span></Label><Input bsSize="sm" value={form.plate_no} onChange={e => set("plate_no", e.target.value)} /></FormGroup></Col>
          </Row>
        </CardBody></Card>

        {/* Vehicle Details */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Vehicle Details</h6>
          <Row>
            <Col md={3}><FormGroup><Label>Vehicle type</Label>
              <Input type="select" bsSize="sm" value={form.vehicle_type} onChange={e => set("vehicle_type", e.target.value)}>
                <option value="">Select type</option>{VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Input></FormGroup></Col>
            <Col md={5}><FormGroup><Label>Name of vehicle</Label><Input bsSize="sm" placeholder="e.g. MITSUBISHI ROSA" value={form.name_of_vehicle} onChange={e => set("name_of_vehicle", e.target.value)} /></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Model year</Label><Input bsSize="sm" value={form.model_year} onChange={e => set("model_year", e.target.value)} /></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Origin</Label><Input bsSize="sm" value={form.vehicle_origin} onChange={e => set("vehicle_origin", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>T.C. number</Label><Input bsSize="sm" value={form.tc_number} onChange={e => set("tc_number", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Engine No.</Label><Input bsSize="sm" value={form.engine_no} onChange={e => set("engine_no", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Chassis No.</Label><Input bsSize="sm" value={form.chassis_no} onChange={e => set("chassis_no", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Seater</Label><Input type="number" bsSize="sm" value={form.seater} onChange={e => set("seater", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Driver</Label><Input bsSize="sm" value={form.driver_name} onChange={e => set("driver_name", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Category</Label><Input bsSize="sm" value={form.category} onChange={e => set("category", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Book No.</Label><Input bsSize="sm" value={form.book_no} onChange={e => set("book_no", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Status</Label>
              <Input type="select" bsSize="sm" value={form.status} onChange={e => set("status", Number(e.target.value))}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </Input></FormGroup></Col>
          </Row>
        </CardBody></Card>

        {/* Registration & Insurance */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Registration &amp; Insurance</h6>
          <Row>
            <Col md={3}><FormGroup><Label>Registration date</Label><Input type="date" bsSize="sm" value={form.registration_date} onChange={e => set("registration_date", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Mulkiya expiry</Label><Input type="date" bsSize="sm" value={form.mulkiya_expiry_date} onChange={e => set("mulkiya_expiry_date", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Insurance policy no.</Label><Input bsSize="sm" value={form.insurance_policy_number} onChange={e => set("insurance_policy_number", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Insurance expiry</Label><Input type="date" bsSize="sm" value={form.insurance_expiry_date} onChange={e => set("insurance_expiry_date", e.target.value)} /></FormGroup></Col>
          </Row>
        </CardBody></Card>

        {/* Finance / Asset */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Finance / Asset</h6>
          <Row>
            <Col md={4}><FormGroup><Label>Expense account</Label>
              <Input type="select" bsSize="sm" value={form.expense_group_id}
                onChange={e => set("expense_group_id", e.target.value)}>
                <option value="">Select expense account</option>
                {expenseGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Input>
              <small className="text-muted">
                {expenseGroups.length
                  ? "Which expense group this vehicle's running costs book to."
                  : "No expense accounts configured — set them in Developer → Fleet Accounts."}
              </small>
            </FormGroup></Col>
            <Col md={3}><FormGroup><Label>Opening balance</Label>
              <Input type="number" step="0.01" min="0" bsSize="sm" value={form.opening_balance} onChange={e => set("opening_balance", e.target.value)} />
              <small className="text-muted">Asset value → the vehicle's Fixed-Asset ledger.</small>
            </FormGroup></Col>
            <Col md={3}><FormGroup><Label>Depreciation cost</Label>
              <Input type="number" step="0.01" min="0" bsSize="sm" value={form.depreciation_cost} onChange={e => set("depreciation_cost", e.target.value)} />
            </FormGroup></Col>
          </Row>
        </CardBody></Card>

        {/* Salik / VIP / GPS */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Salik / VIP / GPS</h6>
          <Row>
            <Col md={3}><FormGroup><Label>Salik tag ID</Label><Input bsSize="sm" value={form.salik_tag_id} onChange={e => set("salik_tag_id", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Salik account no.</Label><Input bsSize="sm" value={form.salik_account_no} onChange={e => set("salik_account_no", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>VIP tag no.</Label><Input bsSize="sm" value={form.vip_tag_no} onChange={e => set("vip_tag_no", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>GPS ID</Label><Input bsSize="sm" value={form.gps_id} onChange={e => set("gps_id", e.target.value)} /></FormGroup></Col>
          </Row>
        </CardBody></Card>

        {/* Remarks */}
        <Card className="mb-3 me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Remarks</h6>
          <Row>
            <Col md={12}><FormGroup><Input type="textarea" rows={2} bsSize="sm" value={form.remarks} onChange={e => set("remarks", e.target.value)} /></FormGroup></Col>
          </Row>
        </CardBody></Card>
      </div>
    </div>
  )
}

export default FleetEdit
